// pages/ExpensePurchaseOrder/EposReadyForPayment.js
//
// "Ready for Payment" — accounting view of APPROVED EPOs not yet paid.
// Legacy form: Projects/epoReadyForPayment.vb
//
// R41 — replaced the radio facet search with the column-filter row UX.
// The "Ready / Truly Ready" toggle stays (different axis — it's a
// different server-side query, not a search term).
//
// Backend (unchanged):
//   GET  /api/epo-query/ready-for-payment?strict=&countryCode=&page=&pageSize=
//   POST /api/epo/{epoNum}/payment   body { paid: true }
//
// Access: gate prop requireModule="epo.accounting".

import React, { useEffect, useState, useCallback, useMemo } from "react";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import { exportAllPagesToCsv, fetchAllPages } from "../../Components/Common/DashboardKit/exportCsv";
// Round EPO-Final — legal-entity filter vocabulary + labels (one source).
import { EPO_COMPANY_FILTER_OPTIONS, epoCompanyFilterCodeOf, getEpoCompanyDisplayLabel } from "./epoCompanyBranding";
import {
    Container, Card, CardBody, Input, Badge, Alert,
    Button, ButtonGroup,
    Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader,
    EmptyState,
    TableSkeleton,
    ColumnFilterRow,
    applyColumnFilters,
    // R44/R46 — applyColumnFilters dropped (server filters now);
    //         useDebouncedValue dropped (text inputs now commit on Enter/blur).
} from "../../Components/Common/DashboardKit";
import api from "../../api";
import useEpoReviewModal from "../../hooks/useEpoReviewModal";

const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};
const pick = (row, ...keys) => {
    for (const k of keys) {
        if (row != null && row[k] !== undefined && row[k] !== null) return row[k];
    }
    return undefined;
};
const payTypeLabel = (raw) => {
    const s = String(raw || "").trim().toUpperCase();
    if (s === "CHECK") return "Check Request";
    if (s === "PAY")   return "Pay When Billed";
    if (s === "CASH")  return "Cash Advance";
    return raw || "—";
};

const EposReadyForPayment = () => {
    const navigate = useNavigate();
    // Holds the latest "reload the list with current filters/page" function.
    // Filled in below (after `load` and the paging state exist) and kept current
    // each render, so the modal's onActionDone always calls the freshest reload.
    const reloadRef = React.useRef(() => {});
    const epoReview = useEpoReviewModal({
        permissions: { canPay: true, canFinanceEdit: true, canReceive: true },
        // Fires only after a state-changing action inside the modal (Mark
        // Processed/Paid, Void, etc.). Reload so a now-paid/void EPO drops off
        // this "ready for payment" list and the totals update.
        onActionDone: () => reloadRef.current && reloadRef.current(),
    });

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [strict, setStrict] = useState(false);
    const [paying, setPaying] = useState(null);

    // R41 — column filters.
    const [columnFilters, setColumnFilters] = useState({});
    // R46 — columnFilters dropped; columnFilters is itself the
    //         committed value (ColumnFilterRow defers text updates until
    //         Enter or focus-out).

    // Pagination.
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Round 31 — "all rows" mode: first sort click loads the COMPLETE
    // dataset (same page-walking as Export all). Sorting, filters, and
    // paging then operate on ALL rows, instantly and client-side.
    const [allRows, setAllRows] = useState(null);
    const [loadingAll, setLoadingAll] = useState(false);
    const loadAllRows = async () => {
        if (loadingAll || allRows) return;
        setLoadingAll("Loading all…");
        try {
            const fetched = await fetchAllPages({
                onProgress: (n, t) => setLoadingAll(`Loading all ${n} / ${t}…`),
                fetchPage: async (pageNum, sizeNum) => {
                    const params = { page: pageNum, pageSize: sizeNum, strict: !!strict };
                    const { data } = await api.get("epo-query/ready-for-payment", { params });
                    const list = Array.isArray(data?.Items) ? data.Items
                               : Array.isArray(data?.items) ? data.items
                               : Array.isArray(data?.results) ? data.results
                               : Array.isArray(data?.Results) ? data.Results : [];
                    return { rows: list, total: data?.Total ?? data?.total ?? 0 };
                },
            });
            // Guard: if the full-fetch came back empty but the server reports
            // rows exist (total > 0), do NOT switch into all-rows mode — that
            // would blank a grid that actually has data. Stay in server mode.
            if (Array.isArray(fetched) && fetched.length === 0 && total > 0) {
                setLoadingAll(false);
                return;
            }
            setAllRows(fetched);
            setPage(1);
        } catch (e) { /* keep server mode on failure */ }
        setLoadingAll(false);
    };
    // Round 29 — full CSV export: same endpoint + filters as the table,
    // looped across all pages (the visible page is just a window).
    const [exporting, setExporting] = useState(false);
    const handleExportAll = async () => {
        try {
            setExporting("Preparing…");
            await exportAllPagesToCsv({
                filename: "epos-ready-for-payment-all.csv",
                columns: columns.filter((c) => c.type !== "none"),
                total,
                onProgress: (n, t) => setExporting(`Exporting ${n} / ${t}…`),
                fetchPage: async (pageNum, sizeNum) => {
                    const params = { page: pageNum, pageSize: sizeNum, strict: !!strict };
                    for (const [k, v] of Object.entries(columnFilters || {})) {
                        if (v == null || v === "") continue;
                        params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
                    }
                    const { data } = await api.get("epo-query/ready-for-payment", { params });
                    const list = Array.isArray(data?.Items) ? data.Items
                               : Array.isArray(data?.items) ? data.items
                               : Array.isArray(data?.results) ? data.results
                               : Array.isArray(data?.Results) ? data.Results : [];
                    return { rows: list, total: data?.Total ?? data?.total ?? 0 };
                },
            });
        } catch (e) {
            setExporting(false);
            return;
        }
        setExporting(false);
    };


    // R44 — server-side column filtering.
    const load = useCallback((strictVal, pageNum, sizeNum, filters) => {
        let alive = true;
        setLoading(true);
        setError(null);
        const params = {
            strict: !!strictVal,
            countryCode: "USA",
            page: pageNum ?? 1,
            pageSize: sizeNum ?? 25,
        };
        for (const [k, v] of Object.entries(filters || {})) {
            if (v == null || v === "") continue;
            params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
        }
        api.get("epo-query/ready-for-payment", { params })
            .then(({ data }) => {
                if (!alive) return;
                const items = data?.Items ?? data?.items ?? [];
                setRows(Array.isArray(items) ? items : []);
                const t  = data?.Total      ?? data?.total      ?? 0;
                const tp = data?.TotalPages ?? data?.totalPages ?? 1;
                const sp = data?.Page       ?? data?.page       ?? (pageNum ?? 1);
                setTotal(t);
                setTotalPages(Math.max(1, tp));
                setPage(sp);
            })
            .catch((e) => {
                if (!alive) return;
                const st = e?.response?.status;
                setError(
                    st === 403 || st === 401
                        ? "You are not authorized to view payment-ready EPOs."
                        : (e?.response?.data?.Message || e?.message || "Failed to load.")
                );
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    // Keep the modal's onActionDone reload pointing at the current state, so
    // after Mark Processed / Void the list reloads with the same strict toggle,
    // page, page size, and column filters the user is currently viewing.
    reloadRef.current = () => {
        if (allRows) {
            // "all loaded" mode filters client-side; re-pull the full set.
            if (typeof loadAllRows === "function") loadAllRows();
        } else {
            load(strict, page, pageSize, columnFilters);
        }
    };

    // R44 — strict toggle resets filters + paging.
    useEffect(() => {
        setColumnFilters({});
        setPage(1);
        setAllRows(null); // Round 31 — strict is a server param
        return load(strict, 1, pageSize, {});
        // eslint-disable-next-line
    }, [strict]);

    // R44 — reload when filters change.
    useEffect(() => {
        setPage(1);
        if (allRows) return; // Round 31 — all-mode filters client-side
        return load(strict, 1, pageSize, columnFilters);
        // eslint-disable-next-line
    }, [columnFilters]);

    const goToPage = (n) => {
        const t = Math.max(1, Math.min(effTotalPages, n));
        if (t === page) return;
        if (allRows) { setPage(t); return; } // Round 31 — local paging
        load(strict, t, pageSize, columnFilters);  // R44
    };
    const changePageSize = (sz) => {
        const s2 = Number(sz) || 25;
        setPageSize(s2);
        if (allRows) { setPage(1); return; }
        load(strict, 1, s2, columnFilters);  // R44
    };

    const markPaid = async (epoNum) => {
        setNotice(null);
        setPaying(epoNum);
        try {
            await api.post(`epo/${epoNum}/payment`, { paid: true });
            setRows((prev) => prev.filter((r) => pick(r, "EpoNumber", "epoNumber") !== epoNum));
            setTotal((t) => Math.max(0, t - 1));
            setNotice(`EPO #${epoNum} marked as paid.`);
        } catch (e) {
            setError(e?.response?.data?.Message || e?.message || `Failed to mark EPO #${epoNum} paid.`);
        } finally {
            setPaying(null);
        }
    };

    const columns = useMemo(() => [
        { id: "epoNumber",   label: "EPO #",         type: "text",
          get: r => pick(r, "EpoNumber", "epoNumber") },
        { id: "createdDate", label: "Created",       type: "text",
          get: r => fmtDate(pick(r, "CreatedDate", "createdDate")) },
        { id: "createdBy",   label: "Created By",    type: "text",
          get: r => pick(r, "CreatedByName", "createdByName", "CreatedBy", "createdBy") },
        { id: "approvedBy",  label: "Approved By",   type: "text",
          get: r => pick(r, "ApprovedBy", "approvedBy") },
        // Round EPO-Final — relabeled: this column has ALWAYS been the
        // Vendor/Payee (EpoHeader.Company); the label now says so because a
        // real Legal Entity column exists right beside it.
        { id: "company",     label: "Vendor / Payee", type: "text",
          get: r => pick(r, "Company", "company") },
        // Round EPO-Final — the EPO's OWN legal entity. get() returns the
        // canonical filter code (LEGACY for pre-column NULLs) so the select
        // filter value matches server-side (filterOwningCompany) AND
        // client-side (all-rows mode) with one vocabulary; the cell + CSV
        // show it via this code (stable identifier).
        { id: "owningCompany", label: "Legal Entity", type: "select",
          options: EPO_COMPANY_FILTER_OPTIONS,
          get: r => epoCompanyFilterCodeOf(pick(r, "OwningCompany", "owningCompany")) },
        { id: "epoType",     label: "Type",          type: "text",
          get: r => pick(r, "EpoType", "epoType") },
        { id: "paymentType", label: "Payment Type",  type: "text",
          get: r => payTypeLabel(pick(r, "PaymentType", "paymentType")) },
        { id: "received",    label: "Received",      type: "boolean",
          get: r => !!pick(r, "ItemsRecvd", "itemsRecvd") },
        { id: "attachment",  label: "Attachment",    type: "boolean",
          get: r => !!pick(r, "HasAttachment", "hasAttachment") },
        { id: "approxTotal", label: "Approx. Total", type: "text",
          get: r => {
            const v = pick(r, "ApproxTotal", "approxTotal");
            return v != null ? String(v) : "";
          }},
        { id: "actions",     label: "",              type: "none" },
    ], []);

    // R44 — server returns already-filtered rows.
    const visibleRowsBase = allRows
        ? applyColumnFilters(allRows, columns, columnFilters)
        : rows;
    // Round 30/31 — first sort click loads ALL rows; sort covers the dataset.
    const { sorted: visibleRows, sort, toggleSort } = useTableSort(visibleRowsBase, columns);
    const onSortToggle = (id) => {
        if (!allRows && !loadingAll) loadAllRows();
        toggleSort(id);
    };
    const effTotal = allRows ? visibleRows.length : total;
    const effTotalPages = allRows ? Math.max(1, Math.ceil(visibleRows.length / pageSize)) : totalPages;
    const pagedRows = allRows
        ? visibleRows.slice((page - 1) * pageSize, page * pageSize)
        : visibleRows;

    const w = 7;
    let lo = Math.max(1, page - 3);
    let hi = Math.min(effTotalPages, lo + w - 1);
    lo = Math.max(1, hi - w + 1);
    const nums = [];
    for (let i = lo; i <= hi; i++) nums.push(i);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="EPOs Ready for Payment"
                    breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: "EPOs Ready for Payment" }]}
                    subtitle={`Approved EPOs awaiting payment (${total} total)`}
                    breadcrumbItems={[{ label: "EPO", href: "/epo" }, { label: "Ready for Payment" }]}
                />

                {error && <Alert color="danger" className="mt-2" toggle={() => setError(null)}>{error}</Alert>}
                {notice && <Alert color="success" className="mt-2" toggle={() => setNotice(null)}>{notice}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        {/* Ready / Truly Ready toggle — different axis, kept. */}
                        <div className="mb-3">
                            <ButtonGroup size="sm">
                                <Button color={!strict ? "primary" : "light"} onClick={() => setStrict(false)}>Ready</Button>
                                <Button color={strict ? "primary" : "light"} onClick={() => setStrict(true)}>Truly Ready</Button>
                            </ButtonGroup>
                        </div>

                        {loading ? (
                            <div className="table-responsive epo-sticky-head">
                                    <table className="table table-striped table-bordered mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>{columns.map((c) => <th key={c.id}>{c.label}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            <TableSkeleton rows={6} columns={columns.length} />
                                        </tbody>
                                    </table>
                                </div>
                        ) : rows.length === 0 ? (
                            <EmptyState
                                title={strict ? "Nothing truly ready" : "Nothing ready for payment"}
                                description={strict
                                    ? "No approved EPOs currently have an attachment and are received/cash."
                                    : "No approved-but-unpaid EPOs right now."}
                            />
                        ) : (
                            <>
                                                                {/* Round 29 — exports ALL rows matching the current filters (walks the same API, all pages) */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm" disabled={!!exporting || !total} onClick={handleExportAll}>
                                        <i className="ri-download-2-line align-bottom me-1" />
                                        {exporting ? exporting : `Export all CSV (${total})`}
                                    </button>
                                                                    {loadingAll ? (
                                        <span className="badge bg-primary-subtle text-primary align-self-center ms-2">{loadingAll}</span>
                                    ) : allRows ? (
                                        <span className="badge bg-success-subtle text-success align-self-center ms-2" title="Sorting and filters now cover the full dataset">All {allRows.length} rows loaded</span>
                                    ) : null}
                                </div>
<div className="table-responsive epo-sticky-head">
                                    <table className="table table-striped table-bordered mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                {columns.map((c) => (
                                                    <SortableTh key={c.id} col={c} sort={sort} onToggle={onSortToggle}
                                                        className={c.id === "approxTotal" ? "text-end" : ""} />
                                                ))}
                                            </tr>
                                            <ColumnFilterRow
                                                columns={columns}
                                                filters={columnFilters}
                                                onChange={setColumnFilters}
                                            />
                                        </thead>
                                        <tbody>
                                            {pagedRows.map((r) => {
                                                const epoNum = pick(r, "EpoNumber", "epoNumber");
                                                const recvd  = !!pick(r, "ItemsRecvd", "itemsRecvd");
                                                const att    = !!pick(r, "HasAttachment", "hasAttachment");
                                                return (
                                                    <tr key={epoNum} onDoubleClick={() => epoReview.open(epoNum)} style={{ cursor: "pointer" }}>
                                                        <td><strong>{epoNum}</strong></td>
                                                        <td>{fmtDate(pick(r, "CreatedDate", "createdDate"))}</td>
                                                        <td>{pick(r, "CreatedByName", "createdByName", "CreatedBy", "createdBy") ?? "—"}</td>
                                                        <td>{pick(r, "ApprovedBy", "approvedBy") ?? "—"}</td>
                                                        <td>{pick(r, "Company", "company") ?? "—"}</td>
                                                        <td>{getEpoCompanyDisplayLabel(pick(r, "OwningCompany", "owningCompany"))}</td>
                                                        <td>{pick(r, "EpoType", "epoType") ?? "—"}</td>
                                                        <td>{payTypeLabel(pick(r, "PaymentType", "paymentType"))}</td>
                                                        <td>
                                                            <Badge color={recvd ? "success" : "secondary"} pill style={{ fontSize: 11 }}>
                                                                {recvd ? "Yes" : "No"}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <Badge color={att ? "success" : "secondary"} pill style={{ fontSize: 11 }}>
                                                                {att ? "Yes" : "No"}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-end">{fmtMoney(pick(r, "ApproxTotal", "approxTotal"))}</td>
                                                        <td className="text-nowrap">
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm p-0 me-2"
                                                                onClick={() => epoReview.open(epoNum)}
                                                            >View</button>
                                                            <Button
                                                                color="success"
                                                                size="sm"
                                                                disabled={paying === epoNum}
                                                                onClick={() => markPaid(epoNum)}
                                                            >
                                                                {paying === epoNum ? "…" : "Mark Paid"}
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {visibleRows.length === 0 && (
                                    <div className="text-muted text-center py-3 small">
                                        No rows match the current filters.
                                    </div>
                                )}

                                {total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-muted small">Rows per page:</span>
                                            <Input type="select" bsSize="sm" value={pageSize}
                                                onChange={(e) => changePageSize(e.target.value)} style={{ width: 80 }}>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                            </Input>
                                            <span className="text-muted small">
                                                {(allRows || visibleRows.length === rows.length)
                                                    ? <>Showing {effTotal === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, effTotal)} of {effTotal}{allRows ? " (all loaded)" : ""}</>
                                                    : <>Showing {visibleRows.length} of {rows.length} on this page (of {total} total)</>
                                                }
                                            </span>
                                        </div>
                                        <Pagination size="sm" className="mb-0">
                                            <PaginationItem disabled={page <= 1}><PaginationLink first onClick={() => goToPage(1)} /></PaginationItem>
                                            <PaginationItem disabled={page <= 1}><PaginationLink previous onClick={() => goToPage(page - 1)} /></PaginationItem>
                                            {nums.map((n) => (
                                                <PaginationItem key={n} active={n === page}>
                                                    <PaginationLink onClick={() => goToPage(n)}>{n}</PaginationLink>
                                                </PaginationItem>
                                            ))}
                                            <PaginationItem disabled={page >= effTotalPages}><PaginationLink next onClick={() => goToPage(page + 1)} /></PaginationItem>
                                            <PaginationItem disabled={page >= effTotalPages}><PaginationLink last onClick={() => goToPage(effTotalPages)} /></PaginationItem>
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        {epoReview.element}
        </div>
    );
};

export default EposReadyForPayment;
